// Lightweight inline disclosure (ghost-button trigger, no card chrome) for an
// optional/advanced group nested inside other content. To collapse a whole
// bordered Card from its own header, use `CardSection` with `collapsible` instead.
import { ReactNode, useState } from 'react';
import { cn } from '../../../lib/utils';
import { DisclosureChevron } from './DisclosureChevron';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import { Button } from '../../../components/ui/button';

interface CollapsibleSectionProps {
  /** Section label shown in the trigger */
  label: string;
  /** Summary shown inline when collapsed (string or ReactNode) */
  summary?: ReactNode;
  /** Content to show when expanded */
  children: ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Start open by default (uncontrolled mode) */
  defaultOpen?: boolean;
  /** Additional className on the root */
  className?: string;
  /** Additional className on the content wrapper */
  contentClassName?: string;
}

export function CollapsibleSection({
  label,
  summary,
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
  className,
  contentClassName,
}: CollapsibleSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = (value: boolean) => {
    if (!isControlled) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange} className={className}>
      <CollapsibleTrigger asChild>
        {/* `max-w-full` is what keeps the trigger inside its container, and it is
            the whole fix. The Button is an inline-flex box sized to fit-content,
            and every child inherits its `whitespace-nowrap`, so both spans'
            min-content contributions are their FULL text widths — fit-content
            therefore resolves to MAX-content and the trigger renders past the
            container instead of clipping. `truncate` on the summary cannot save
            it (it only lets that span shrink once the box itself is capped), and
            neither can a fixed `max-w-[Npx]`, which is wider than a 375px screen
            leaves once card and section padding are taken; the cap survives only
            behind `sm:`, where it is a deliberate desktop ceiling.

            The label carries no `truncate` on purpose: with `overflow: visible`
            its flex minimum is its own min-content width, so it cannot shrink and
            the whole deficit lands on the summary — the label names the control
            and must stay readable. `overflow-hidden` on the trigger is the floor
            under that: a label longer than the container on its own is clipped by
            the button box rather than painted past the viewport edge. */}
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground gap-1 px-1 h-auto py-1 max-w-full min-w-0 overflow-hidden"
        >
          <DisclosureChevron open={isOpen} />
          <span>{label}</span>
          {!isOpen && summary && (
            <span className="ms-1 min-w-0 truncate text-xs font-normal text-muted-foreground/70 sm:max-w-[300px]">
              {summary}
            </span>
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn('space-y-3 pt-2', contentClassName)}>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
